import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useToast } from '@app/components/ui/Toast';
import { authService } from '@app/services/auth.service';
import { meService } from '@app/services/me.service';
import { notificationsService } from '@app/services/notifications.service';

type AuthContextValue = {
  isAuth?: AuthState;
  setIsAuth: (value: AuthState) => void;
  info?: any;
  setInfo: (value: any) => void;
  refreshMe: () => Promise<void>;
  isVerified?: boolean;
  setIsVerified: (value: boolean) => void;
  logout: (isExpired: boolean) => Promise<void>;
  isLoggedOut: boolean;
};

export enum AuthState {
  INIT = 'INIT',
  AUTH = 'AUTH',
  UNAUTH = 'UNAUTH',
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const toast = useToast();
  const [isAuth, setIsAuth] = useState<AuthState>(AuthState.INIT);
  const [dataMe, setDataMe] = useState<any>();
  const [isVerified, setIsVerified] = useState<boolean>();
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(false);
  const setIsAuthSafe = useCallback((v: AuthState) => setIsAuth(v), []);

  const logout = useCallback(
    async (isExpired: boolean = true) => {
      try {
        await authService.logout();
      } finally {
        setIsAuth(AuthState.UNAUTH);
        setDataMe(undefined);
        setIsVerified(false);
        if (isExpired) {
          sessionStorage.setItem('returnUrl', router.asPath);
          toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại');
        } else {
          setIsLoggedOut(true);
          toast.success('Đăng xuất thành công');
          setTimeout(() => {
            setIsLoggedOut(false);
          }, 1000);
        }
      }
    },
    [setIsAuth]
  );

  const refreshMe = useCallback(async () => {
    try {
      const { success, data, errors } = await meService.info();
      if (!success || errors) {
        toast.error(
          errors?.[0]?.message?.vn ?? 'Thông tin người dùng không hợp lệ. Vui lòng đăng nhập lại'
        );
        logout(false);
        router.push('/auth/login');

        return;
      }

      setDataMe(data);
      setIsVerified(data?.isVerified ?? false);
      setIsAuthSafe(AuthState.AUTH);
    } catch {
      document.cookie = 'auth=0; path=/; sameSite=lax';
      setDataMe(undefined);
      setIsAuth(AuthState.UNAUTH);
      setIsVerified(false);
    }
  }, [setIsAuthSafe]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Đọc cookie trạng thái auth do middleware set
    try {
      const cookieAuth = document.cookie
        .split(';')
        .map(s => s.trim())
        .find(s => s.startsWith('auth='));

      if (cookieAuth) {
        const val = cookieAuth.split('=')[1];
        setIsAuthSafe(val === '1' ? AuthState.AUTH : AuthState.UNAUTH);
      } else {
        // Nếu không tìm thấy cookie auth, set về UNAUTH
        setIsAuthSafe(AuthState.UNAUTH);
      }
    } catch {
      document.cookie = 'auth=0; path=/; sameSite=lax';
      setIsAuthSafe(AuthState.UNAUTH);
    }
  }, [setIsAuthSafe]);

  useEffect(() => {
    if (window === undefined) return;

    (window as any).logout = logout;
  }, [logout]);

  useEffect(() => {
    if (isAuth === AuthState.AUTH && !dataMe?.isVerified) refreshMe();
  }, [refreshMe, isAuth]);

  useEffect(() => {
    if (isAuth === AuthState.AUTH && dataMe && dataMe.hasActiveCert === undefined) {
      meService
        .certificates({ status: ['ACTIVE'] })
        .then(({ data }) => {
          if (data && data.length > 0) {
            setDataMe({
              ...dataMe,
              hasActiveCert: data.length > 0 && dayjs(data[0].validTo).isAfter(dayjs(), 'second'),
            });
          }
        })
        .catch(error => {
          console.error('Error fetching user info:', error);
        });
    }
  }, [isAuth, dataMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuth,
      setIsAuth: setIsAuthSafe,
      info: dataMe,
      setInfo: setDataMe,
      refreshMe,
      isVerified,
      logout,
      setIsVerified,
      isLoggedOut,
    }),
    [
      isAuth,
      setIsAuthSafe,
      dataMe,
      refreshMe,
      setDataMe,
      isVerified,
      logout,
      setIsVerified,
      isLoggedOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);

  if (!ctx) throw new Error('useAuth must be used within AuthProvider');

  return ctx;
}
